/**
 * Set a follow-up reminder (item #5).
 *
 * Opens after an order is placed, after a delivery, and from the customer
 * screen. Event type, a date (quick chips or a picker), an optional time, and a
 * note. One client_uuid per open so a double-tap sets one reminder.
 */

import { useMemo, useRef, useState } from 'react';
import { Sheet, Button, ChipRow, TextArea, TextInput, useToast } from './ui';
import { api, ApiError } from '../lib/api';

type EventType = 'order' | 'collection' | 'call' | 'visit' | 'other';

const EVENT_OPTIONS: { value: EventType; label: string }[] = [
  { value: 'order', label: 'Next order' },
  { value: 'collection', label: 'Collect money' },
  { value: 'call', label: 'Call' },
  { value: 'visit', label: 'Visit' },
  { value: 'other', label: 'Other' },
];

const TIME_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '09:00', label: 'Morning' },
  { value: '14:00', label: 'Afternoon' },
  { value: '18:00', label: 'Evening' },
];
const BAND_TIMES = new Set(['09:00', '14:00', '18:00']);

function isoAdd(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function ReminderSheet({
  open, onClose, customerId, customerName, orderId, source = 'manual', onDone,
}: {
  open: boolean;
  onClose: () => void;
  customerId: string;
  customerName?: string;
  orderId?: string;
  source?: 'order' | 'delivery' | 'manual';
  onDone?: () => void;
}) {
  const toast = useToast();
  const [eventType, setEventType] = useState<EventType>(source === 'order' ? 'order' : 'call');
  const [dueDate, setDueDate] = useState(isoAdd(1));
  // The band chip ('' = any, or a preset HH:MM), and an optional exact 24-hour
  // time used when the band is "Any time".
  const [timeBand, setTimeBand] = useState('');
  const [exactTime, setExactTime] = useState('');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const uuid = useRef<string | null>(null);

  const dateOptions = useMemo(() => [
    { value: isoAdd(1), label: 'Tomorrow' },
    { value: isoAdd(2), label: 'In 2 days' },
    { value: isoAdd(3), label: 'In 3 days' },
    { value: isoAdd(7), label: 'Next week' },
  ], []);

  async function save() {
    if (!dueDate) { toast.show('Pick a date.', 'danger'); return; }
    // A band chip wins; otherwise the exact 24-hour time, if one was entered.
    const dueTime = BAND_TIMES.has(timeBand) ? timeBand : (exactTime || undefined);
    setBusy(true);
    if (!uuid.current) uuid.current = crypto.randomUUID();
    try {
      await api.post('/api/reminders', {
        customerId,
        orderId: orderId || undefined,
        eventType,
        dueDate,
        dueTime,
        detail: detail.trim() || undefined,
        source,
        clientUuid: uuid.current,
      });
      uuid.current = null;
      toast.show('Reminder set.', 'ok');
      onDone?.();
      onClose();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : 'Could not set the reminder', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={customerName ? `Set a reminder — ${customerName}` : 'Set a reminder'}
      footer={
        <>
          <Button variant="ghost" block onClick={onClose} disabled={busy}>Skip</Button>
          <Button block busy={busy} onClick={() => void save()}>Set reminder</Button>
        </>
      }
    >
      <div className="stack-3">
        <div className="stack-1">
          <div className="t-sm t-semi">Event type</div>
          <ChipRow value={eventType} onChange={(v) => setEventType(v as EventType)} options={EVENT_OPTIONS} wrap />
        </div>

        <div className="stack-1">
          <div className="t-sm t-semi">When</div>
          <ChipRow value={dueDate} onChange={setDueDate} options={dateOptions} wrap />
          <TextInput type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>

        <div className="stack-1">
          <div className="t-sm t-semi">Time</div>
          <ChipRow
            value={timeBand}
            onChange={(v) => { setTimeBand(v); if (v) setExactTime(''); }}
            options={TIME_OPTIONS}
            wrap
          />
          {/* "Any time" reveals an exact 24-hour entry — leave it blank for no
              specific time, or set one (HH:MM, 24-hour). */}
          {timeBand === '' && (
            <TextInput
              type="time"
              label="Exact time (24-hour) — optional"
              value={exactTime}
              onChange={(e) => setExactTime(e.target.value)}
            />
          )}
        </div>

        <TextArea
          label="Detail (optional)"
          placeholder="Will reorder 5 cartons Campa after Diwali"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          rows={2}
        />
      </div>
    </Sheet>
  );
}
